/**
 * Presentation3DViewer — document-like view for posts of doc_type
 * '3dpresentation' or '3d_presentation'.
 *
 * Layout (top to bottom):
 *   1. Hero: cover_image_url, else first image/video in media[], else a
 *      branded "3D" placeholder block.
 *   2. Subtitle if present.
 *   3. Settings table: nicely-labelled metadata_json fields.
 *   4. CTA: "Open 3D Presentation ↗" link to the actual 3DPresenter app.
 *   5. (Full mode only) "Source" toggle to peek at raw markdown for debug.
 *
 * The actual 3D iframe is reached via the Open button — keeps the in-portal
 * view fast and document-shaped, leaves the interactive 3D experience to
 * the dedicated app.
 */

import { useState } from 'react'
import { PRESENTER_3D } from '../config'
import type { MediaItem, Post } from '../types'

interface Props {
  post: Post
  /** Compact mode for embed iframes — tighter padding, no Source toggle. */
  compact?: boolean
}

// Pretty labels for known metadata_json keys. Unknown keys fall back to
// snake_case → Title Case conversion via prettifyKey().
const KEY_LABELS: Record<string, string> = {
  layouter: 'Layouter',
  poi_count: 'Slides',
  slide_count: 'Slides',
  video_count: 'Videos',
  deck_origin: 'Origin',
  legacy_storage_id: 'Legacy Storage ID',
  scene_id: 'Scene ID',
  camera: 'Camera',
  background: 'Background',
}

// Keys we never show to end users (internal-only / redundant).
const HIDDEN_KEYS = new Set(['has_videos'])

function prettifyKey(k: string): string {
  if (KEY_LABELS[k]) return KEY_LABELS[k]
  return k
    .replace(/[_-]/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase())
}

function formatValue(v: unknown): string {
  if (v === null || v === undefined) return '—'
  if (typeof v === 'boolean') return v ? 'ja' : 'nein'
  if (Array.isArray(v)) return v.length === 0 ? '—' : v.join(', ')
  if (typeof v === 'object') return JSON.stringify(v)
  return String(v)
}

function pickHero(post: Post): MediaItem | null {
  if (!post.media || post.media.length === 0) return null
  // Prefer images for the hero (videos as fallback)
  const images = post.media.filter(m => !m.role?.includes('video'))
  return (images[0] ?? post.media[0]) ?? null
}

interface ParsedSlide {
  number: number
  uri: string
  isVideo: boolean
  title: string | null
  body: string | null
}

// 3D presentation content is a markdown sequence: slides separated by `---`,
// each slide has an HTML comment block (uri/shape/scale/etc.) and optionally
// a markdown body (`# Title` + description) underneath. The first block is
// GLOBAL config — skipped.
function parseSlides(content: string): ParsedSlide[] {
  if (!content) return []
  const parts = content.split(/\n---\n/)
  const slides: ParsedSlide[] = []
  // Skip the first part — it's the GLOBAL config block.
  for (let i = 1; i < parts.length; i++) {
    const part = parts[i].trim()
    if (!part) continue
    const uriMatch = part.match(/uri:\s*(\S+)/)
    if (!uriMatch) continue
    const uri = uriMatch[1]
    const isVideo = /\.m3u8(\?|$)/i.test(uri) || /\.(mp4|mov|webm)(\?|$)/i.test(uri)
    // Body after closing comment marker `-->`
    const bodyMatch = part.match(/-->\s*\n([\s\S]*)/)
    const rawBody = (bodyMatch ? bodyMatch[1] : '').trim()
    let title: string | null = null
    let body: string | null = null
    if (rawBody) {
      const lines = rawBody.split('\n')
      const titleIdx = lines.findIndex(l => l.trim().startsWith('#'))
      if (titleIdx >= 0) {
        title = lines[titleIdx].replace(/^#+\s*/, '').trim()
        const rest = lines.slice(titleIdx + 1).join('\n').trim()
        body = rest || null
      } else {
        body = rawBody
      }
    }
    slides.push({ number: slides.length + 1, uri, isVideo, title, body })
  }
  return slides
}

function videoPosterUrl(uri: string): string | null {
  // VOD streams expose a poster.jpg alongside master.m3u8
  if (/\.m3u8(\?|$)/i.test(uri)) {
    return uri.replace(/master\.m3u8.*$/, 'poster.jpg')
  }
  return null
}

export function Presentation3DViewer({ post, compact = false }: Props) {
  const [showSource, setShowSource] = useState(false)

  const externalSrc = `${PRESENTER_3D}/?post=${post.id}`
  const hero = pickHero(post)
  const heroUrl = post.cover_image_url || hero?.media_url || null

  const meta = (post.metadata_json as Record<string, unknown> | null) || {}
  const settingsEntries = Object.entries(meta)
    .filter(([k]) => !HIDDEN_KEYS.has(k))
    .map(([k, v]) => ({ key: k, label: prettifyKey(k), value: formatValue(v) }))

  const slides = parseSlides(post.content || '')

  return (
    <div className={`pres3d-doc ${compact ? 'pres3d-doc--compact' : ''}`}>
      {heroUrl ? (
        <img className="pres3d-hero" src={heroUrl} alt={post.title} loading="lazy" />
      ) : (
        <div className="pres3d-hero pres3d-hero--placeholder">
          <span className="pres3d-hero-badge">3D</span>
          <span className="pres3d-hero-title">{post.title}</span>
        </div>
      )}

      {compact && (
        <h3 className="pres3d-doc-title">{post.title}</h3>
      )}

      {post.subtitle && (
        <p className="pres3d-doc-subtitle">{post.subtitle}</p>
      )}

      {settingsEntries.length > 0 && (
        <dl className="pres3d-settings">
          {settingsEntries.map(({ key, label, value }) => (
            <div key={key} className="pres3d-settings-row">
              <dt>{label}</dt>
              <dd>{value}</dd>
            </div>
          ))}
        </dl>
      )}

      {post.tags && post.tags.length > 0 && (
        <div className="pres3d-doc-tags">
          {post.tags.map(t => (
            <span key={t} className="pres3d-doc-tag">{t}</span>
          ))}
        </div>
      )}

      {slides.length > 0 && (
        <div className="pres3d-slides">
          <h3 className="pres3d-slides-heading">Slides ({slides.length})</h3>
          <div className="pres3d-slide-grid">
            {slides.map(s => {
              const poster = s.isVideo ? videoPosterUrl(s.uri) : null
              const thumbSrc = s.isVideo ? poster : s.uri
              return (
                <div key={s.number} className="pres3d-slide-card">
                  <div className="pres3d-slide-thumb">
                    {thumbSrc ? (
                      <img src={thumbSrc} alt={s.title || `Slide ${s.number}`} loading="lazy" />
                    ) : (
                      <div className="pres3d-slide-thumb-placeholder">
                        <span>{s.isVideo ? 'VIDEO' : 'SLIDE'}</span>
                      </div>
                    )}
                    {s.isVideo && <span className="pres3d-slide-video-badge">▶ Video</span>}
                    <span className="pres3d-slide-num">{s.number}</span>
                  </div>
                  {(s.title || s.body) && (
                    <div className="pres3d-slide-meta">
                      {s.title && <div className="pres3d-slide-title">{s.title}</div>}
                      {s.body && <div className="pres3d-slide-body">{s.body}</div>}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}

      <div className="pres3d-doc-cta">
        <a
          className="pres3d-btn pres3d-btn--primary"
          href={externalSrc}
          target={compact ? '_top' : '_blank'}
          rel="noopener noreferrer"
        >
          Open 3D Presentation ↗
        </a>
        {!compact && (
          <button
            type="button"
            className="pres3d-btn"
            onClick={() => setShowSource(s => !s)}
            title={showSource ? 'Hide raw markdown' : 'Show raw markdown source'}
          >
            {showSource ? 'Hide Source' : 'Source'}
          </button>
        )}
      </div>

      {!compact && showSource && (
        <pre className="pres3d-source">{post.content || ''}</pre>
      )}
    </div>
  )
}
